export interface DashboardData {
  active_subscribers: number;
  subscribers: number;
  online_sessions: number;
  monthly_revenue_minor: number;
  open_balance_minor: number;
  open_invoices: number;
  traffic_24h: { upload_bytes: number; download_bytes: number };
  nas: { active: number; disabled: number; total: number };
  recent_subscribers: Record<string, unknown>[];
}
export interface WorkspaceData {
  dashboard: DashboardData;
  subscribers: Record<string, unknown>[];
  packages: Record<string, unknown>[];
  billing: Record<string, unknown>[];
  payments: Record<string, unknown>[];
  expenses: Record<string, unknown>[];
  vouchers: Record<string, unknown>[];
  nas: Record<string, unknown>[];
  sessions: Record<string, unknown>[];
  sites: Record<string, unknown>[];
  edges: Record<string, unknown>[];
  edgeConflicts: Record<string, unknown>[];
}
export interface CredentialPolicy {
  passwordLengthEnabled: boolean;
  passwordComplexityEnabled: boolean;
  passwordMinLength: number;
  passwordMaxLength: number;
  pinMinDigits: number;
  pinMaxDigits: number;
  nasSecretMinLength: number;
  nasSecretMaxLength: number;
}
export interface NotificationSettingsData {
  settings: {
    tenant_id: string;
    in_app_enabled: boolean;
    email_enabled: boolean;
    sms_enabled: boolean;
    email_recipients: string[];
    sms_recipients: string[];
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
    timezone: string;
    version: number;
    updated_at: string;
  };
  rules: {
    id: string;
    event_pattern: string;
    severity: "info" | "warning" | "critical";
    channels: ("in_app" | "email" | "sms")[];
    escalation_minutes: number | null;
    enabled: boolean;
    version: number;
    created_at: string;
    updated_at: string;
  }[];
}

export function newCorrelationId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

interface FriendlyProblem {
  readonly summary: string;
  readonly resolution: string;
}

const friendlyProblems: Readonly<Record<string, FriendlyProblem>> = {
  capacity_entitlement_unavailable: {
    summary: "CHIS cannot verify this tenant's current licence capacity.",
    resolution:
      "Ask your licence administrator to refresh or install the VLH entitlement, then try again.",
  },
  licensed_user_capacity_exceeded: {
    summary: "This tenant has reached its licensed user capacity.",
    resolution:
      "Archive, revoke or expire unused subscribers; cancel unused voucher batches; or ask your licence administrator or VNS to increase the licensed limit.",
  },
  unauthenticated: {
    summary: "Your sign-in session is no longer valid.",
    resolution: "Sign in again and repeat the action.",
  },
  csrf_invalid: {
    summary: "The security token for this page has expired.",
    resolution:
      "Refresh the page, sign in again if requested, and retry the action.",
  },
  forbidden: {
    summary: "Your account is not allowed to perform this action.",
    resolution:
      "Ask a tenant administrator to grant the required role or permission.",
  },
  invalid_request: {
    summary: "Some submitted information is missing or invalid.",
    resolution: "Review the highlighted fields and formats, then submit again.",
  },
  idempotency_conflict: {
    summary:
      "This request identifier was already used for different information.",
    resolution: "Refresh the page and submit the action again.",
  },
  package_version_conflict: {
    summary: "This package was changed by another user.",
    resolution: "Refresh the package, review the latest version, and retry.",
  },
  package_status_conflict: {
    summary:
      "The package cannot move to the requested status from its current state.",
    resolution:
      "Refresh the package and choose an available status transition.",
  },
  invalid_queue_policy: {
    summary: "The bandwidth or burst settings are not valid.",
    resolution:
      "Ensure burst rates exceed normal rates, thresholds are within burst limits, priority is 1–8, and minimum rates do not exceed normal rates.",
  },
  nas_status_conflict: {
    summary: "The NAS status changed before this action completed.",
    resolution: "Refresh the NAS record and retry using its current status.",
  },
  nas_has_active_sessions: {
    summary: "This NAS still has active subscriber sessions.",
    resolution:
      "Disconnect or move the active sessions before disabling the NAS.",
  },
  nas_address_invalid: {
    summary: "The NAS address is not a valid IPv4 or IPv6 address.",
    resolution:
      "Enter only the NAS device IP address, without http://, a port or a subnet suffix.",
  },
  nas_address_exists: {
    summary: "An active or disabled NAS already uses this IP address.",
    resolution:
      "Use the existing NAS record, archive it first, or enter a different device IP address.",
  },
  nas_code_exists: {
    summary:
      "A NAS with this code already exists, possibly as an archived record.",
    resolution:
      "Use the existing NAS record or choose a different unique code. Archived NAS codes remain reserved for audit history.",
  },
  nas_site_unavailable: {
    summary: "The selected site is not active or no longer exists.",
    resolution:
      "Refresh the page and select an active site before adding the NAS.",
  },
  nas_secret_policy_invalid: {
    summary:
      "The RADIUS shared secret does not meet this tenant's configured policy.",
    resolution:
      "Review Settings → Security and enter a secret within the configured length range.",
  },
  radius_projection_unavailable: {
    summary: "CHIS cannot securely publish this NAS credential to RADIUS.",
    resolution:
      "Ask the server administrator to configure the RADIUS projection key, then retry.",
  },
  credential_unavailable: {
    summary: "This credential cannot be displayed or changed.",
    resolution:
      "Refresh the record. Older records created before encrypted credential storage may need a new credential set by an administrator.",
  },
  password_policy_invalid: {
    summary: "The password does not meet this tenant's configured policy.",
    resolution:
      "Use a password within the range configured in Settings → Security.",
  },
  password_complexity_invalid: {
    summary:
      "The password does not meet this tenant's configured complexity policy.",
    resolution:
      "Include an uppercase letter, lowercase letter, number and symbol, or ask an administrator to disable the complexity check.",
  },
  pin_policy_invalid: {
    summary: "The hotspot PIN is invalid.",
    resolution:
      "Enter digits only and use the PIN length configured in Settings → Security.",
  },
  subscriber_username_exists: {
    summary: "This subscriber username is already in use.",
    resolution:
      "Open the existing subscriber to manage or reactivate it, or enter a different username.",
  },
  print_job_not_reprintable: {
    summary: "This print job cannot be reprinted.",
    resolution:
      "Only completed jobs whose printer is still enabled can be reprinted.",
  },
  operator_email_exists: {
    summary: "An operator already uses this email address.",
    resolution:
      "Manage the existing operator instead of creating a duplicate identity.",
  },
  operator_role_invalid: {
    summary: "The selected operator role is unavailable.",
    resolution: "Refresh Users & Access and select a current tenant role.",
  },
  operator_site_unavailable: {
    summary: "The selected operator site is unavailable.",
    resolution: "Refresh Users & Access and select an active site.",
  },
  last_tenant_admin: {
    summary: "The final active tenant administrator cannot lose access.",
    resolution:
      "Create or promote another tenant administrator before suspending, revoking, or changing this account's administrator role.",
  },
  subscriber_dependency_unavailable: {
    summary:
      "The selected site, NAS or package is not available for this subscriber.",
    resolution:
      "Select an active site, NAS and published package, then try again.",
  },
  subscriber_version_conflict: {
    summary: "This subscriber was changed by another user.",
    resolution: "Refresh the subscriber, review the latest values, and retry.",
  },
  subscriber_not_found: {
    summary:
      "The subscriber no longer exists or is not visible to this account.",
    resolution:
      "Refresh the subscriber list and select an existing subscriber.",
  },
  mac_binding_reset_conflict: {
    summary: "The subscriber's MAC binding changed before the reset completed.",
    resolution: "Refresh the subscriber and retry the reset.",
  },
  package_change_invalid: {
    summary: "The requested subscriber package change is not valid.",
    resolution:
      "Choose a different published package and confirm it differs from the current package.",
  },
  package_change_not_pending: {
    summary: "This package-change request is no longer pending.",
    resolution:
      "Refresh the subscriber and create a new request if a change is still required.",
  },
  invalid_voucher_count: {
    summary: "The voucher quantity is outside the allowed range.",
    resolution: "Enter a quantity from 1 to 5,000.",
  },
  invalid_expiry_mode: {
    summary: "The voucher expiry settings are inconsistent.",
    resolution:
      "Provide a fixed expiry date only when using fixed-date expiry.",
  },
  voucher_dependency_unavailable: {
    summary: "The selected site or package cannot be used for vouchers.",
    resolution:
      "Select an active site and a published voucher-compatible package.",
  },
  voucher_codes_already_returned: {
    summary:
      "These voucher codes were already generated and cannot be displayed again.",
    resolution:
      "Use the original secured export or create a new voucher batch.",
  },
  voucher_batch_has_used_items: {
    summary: "This batch contains vouchers that have already been used.",
    resolution:
      "Revoke eligible individual vouchers instead; used vouchers must remain in the audit history.",
  },
  voucher_batch_not_cancellable: {
    summary: "This voucher batch cannot be cancelled in its current state.",
    resolution:
      "Refresh the batch and review its voucher states before trying another action.",
  },
  voucher_transition_invalid: {
    summary: "The voucher cannot move to the requested status.",
    resolution:
      "Refresh it and choose an action allowed for its current state.",
  },
  voucher_not_saleable: {
    summary: "One or more selected vouchers cannot be sold.",
    resolution:
      "Remove vouchers that are used, cancelled, expired, refunded or revoked, then retry.",
  },
  sale_currency_mismatch: {
    summary: "The selected vouchers use different currencies.",
    resolution: "Create separate sales for each currency.",
  },
  invoice_not_payable: {
    summary: "This invoice is not currently payable.",
    resolution: "Refresh it and select an issued or partially paid invoice.",
  },
  payment_currency_mismatch: {
    summary: "The payment currency does not match the invoice.",
    resolution: "Use the invoice currency or select a matching invoice.",
  },
  payment_exceeds_balance: {
    summary: "The payment is greater than the invoice's outstanding balance.",
    resolution: "Enter an amount no greater than the displayed balance.",
  },
  payment_not_refundable: {
    summary: "This payment cannot be refunded in its current state.",
    resolution:
      "Select a successful payment that still has a refundable balance.",
  },
  refund_exceeds_payment: {
    summary: "The refund exceeds the remaining refundable amount.",
    resolution:
      "Reduce the refund to the available amount shown for the payment.",
  },
  refund_allocation_invalid: {
    summary: "The refund cannot be allocated to this invoice payment.",
    resolution:
      "Refresh the payment details and refund only an available allocation.",
  },
  invoice_not_voidable: {
    summary: "This invoice cannot be voided in its current state.",
    resolution:
      "Refresh it and resolve any recorded payments or dependent transactions first.",
  },
  printer_unavailable: {
    summary: "The required printer is offline or not assigned.",
    resolution:
      "Open Settings → Printers, assign an online printer for this role and site, then retry.",
  },
  printing_unavailable: {
    summary: "The printing service is temporarily unavailable.",
    resolution: "Check the print connector and server status, then retry.",
  },
  print_job_not_found: {
    summary: "The print job could not be found.",
    resolution: "Refresh print history and select an existing job.",
  },
  print_job_not_controllable: {
    summary: "This print job can no longer be retried or cancelled.",
    resolution: "Refresh print history and check its current status.",
  },
  voucher_template_not_draft: {
    summary: "Only draft voucher templates can be published.",
    resolution:
      "Create or select a draft template, save it, and publish again.",
  },
  invoice_not_renderable: {
    summary: "CHIS cannot generate this invoice document.",
    resolution:
      "Confirm the invoice exists, is not void, and has valid customer and line information.",
  },
  route_not_found: {
    summary: "The requested feature is not available on this server.",
    resolution:
      "Refresh the application; if the problem remains, ask the administrator to update CHIS.",
  },
  guest_unavailable: {
    summary: "Guest access is temporarily unavailable.",
    resolution:
      "Retry the page. If it continues, ask the server administrator to check the guest-access service configuration.",
  },
  guest_policy_invalid: {
    summary: "The selected guest policy is no longer available.",
    resolution: "Refresh Guest Wi-Fi and select an active sponsorship policy.",
  },
  guest_sponsor_invalid: {
    summary: "The guest sponsorship details could not be accepted.",
    resolution:
      "Check the contact, validity and consent details, then submit the guest again.",
  },
  guest_enrollment_conflict: {
    summary: "This guest enrollment has already changed.",
    resolution:
      "Refresh Guest Wi-Fi and review its current status before retrying.",
  },
  guest_challenge_invalid: {
    summary: "The guest verification code is invalid or expired.",
    resolution:
      "Create a new guest sponsorship and verify the new code within ten minutes.",
  },
  edge_phase_b_unavailable: {
    summary: "Edge Node services are not enabled on this server.",
    resolution:
      "Ask the server administrator to configure and enable Edge Phase B.",
  },
};

function fallbackProblem(status: number): FriendlyProblem {
  if (status === 400)
    return {
      summary: "The server could not accept the submitted information.",
      resolution: "Review the form values and try again.",
    };
  if (status === 401)
    return {
      summary: "Your session is not authenticated.",
      resolution: "Sign in again and repeat the action.",
    };
  if (status === 403)
    return {
      summary: "The server refused this action.",
      resolution:
        "Check your role and licence, or ask a tenant administrator for assistance.",
    };
  if (status === 404)
    return {
      summary: "The requested record or feature was not found.",
      resolution: "Refresh the page and select an existing record.",
    };
  if (status === 409)
    return {
      summary: "The record changed or is not in the required state.",
      resolution: "Refresh the page, review the latest state, and retry.",
    };
  if (status === 429)
    return {
      summary: "Too many requests were sent in a short time.",
      resolution: "Wait a moment and try again.",
    };
  if (status >= 500)
    return {
      summary:
        "CHIS could not complete the request because a service is unavailable.",
      resolution:
        "Try again shortly. If it continues, ask the server administrator to check system health.",
    };
  return {
    summary: "CHIS could not complete the request.",
    resolution:
      "Refresh the page and try again; contact an administrator if the problem continues.",
  };
}

export function friendlyProblemMessage(
  code: string | undefined,
  status: number,
): string {
  const problem =
    (code === undefined ? undefined : friendlyProblems[code]) ??
    fallbackProblem(status);
  return `${problem.summary} How to fix: ${problem.resolution}`;
}

async function responseError(response: Response): Promise<Error> {
  const problem = (await response.json().catch(() => ({}))) as {
    code?: unknown;
  };
  return new Error(
    friendlyProblemMessage(
      typeof problem.code === "string" ? problem.code : undefined,
      response.status,
    ),
  );
}

async function get<T>(
  path: string,
  tenantId: string,
  token: string,
): Promise<T> {
  const separator = path.includes("?") ? "&" : "?";
  const response = await fetch(
    `${path}${separator}tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return ((await response.json()) as { data: T }).data;
}

export interface SubscriberPage {
  data: Record<string, unknown>[];
  total: number;
  limit: number;
  offset: number;
}
export async function loadSubscriberPage(
  tenantId: string,
  token: string,
  options: {
    limit: number;
    offset: number;
    search?: string;
    status?: string;
    packageId?: string;
    siteId?: string;
  },
): Promise<SubscriberPage> {
  const query = new URLSearchParams({
    tenant_id: tenantId,
    limit: String(options.limit),
    offset: String(options.offset),
  });
  if (options.search) query.set("search", options.search);
  if (options.status) query.set("status", options.status);
  if (options.packageId) query.set("package_id", options.packageId);
  if (options.siteId) query.set("site_id", options.siteId);
  const response = await fetch(`/api/v1/subscribers?${query}`, {
    headers: token === "" ? {} : { authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as SubscriberPage;
}
export async function loadSubscriberConnections(
  tenantId: string,
  token: string,
  subscriberId: string,
  limit: number,
  offset: number,
): Promise<SubscriberPage> {
  const query = new URLSearchParams({
    tenant_id: tenantId,
    limit: String(limit),
    offset: String(offset),
  });
  const response = await fetch(
    `/api/v1/subscribers/${encodeURIComponent(subscriberId)}/connections?${query}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as SubscriberPage;
}

export async function loadCredentialPolicy(
  tenantId: string,
  token: string,
): Promise<CredentialPolicy> {
  const response = await fetch(
    `/api/v1/settings/credential-policy?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as CredentialPolicy;
}
export async function loadNotificationSettings(
  tenantId: string,
  token: string,
): Promise<NotificationSettingsData> {
  const response = await fetch(
    `/api/v1/notifications/settings?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as NotificationSettingsData;
}
export interface IntegrationSettingsData {
  credentials: Array<{
    id: string;
    name: string;
    token_prefix: string;
    permissions: string[];
    status: string;
    expires_at: string | null;
    last_used_at: string | null;
    created_at: string;
  }>;
  endpoints: Array<{
    id: string;
    name: string;
    url: string;
    event_patterns: string[];
    status: "active" | "disabled";
    version: number;
    created_at: string;
    updated_at: string;
  }>;
  deliveries: Array<{
    id: string;
    endpoint_id: string;
    event_type: string;
    status: string;
    attempts: number;
    response_status: number | null;
    last_error_code: string | null;
    delivered_at: string | null;
    created_at: string;
    updated_at: string;
  }>;
}
export async function loadIntegrationSettings(
  tenantId: string,
  token: string,
): Promise<IntegrationSettingsData> {
  const response = await fetch(
    `/api/v1/integrations?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as IntegrationSettingsData;
}

export interface AuditPage { rows: Array<Record<string,unknown>>; total: number; limit: number; offset: number }
export async function loadAuditEvents(tenantId:string,token:string,options:{search?:string;action?:string;actorId?:string;resourceType?:string;resourceId?:string;from?:string;to?:string;limit:number;offset:number}):Promise<AuditPage>{const query=new URLSearchParams({tenant_id:tenantId,limit:String(options.limit),offset:String(options.offset)});for(const[key,value]of Object.entries({search:options.search,action:options.action,actor_id:options.actorId,resource_type:options.resourceType,resource_id:options.resourceId,from:options.from,to:options.to}))if(value)query.set(key,value);const response=await fetch(`/api/v1/audit-events?${query}`,{headers:token===""?{}:{authorization:`Bearer ${token}`},cache:"no-store"});if(!response.ok)throw await responseError(response);return await response.json() as AuditPage;}
export async function loadAuditGovernance(tenantId:string,token:string){const response=await fetch(`/api/v1/audit-events/governance?tenant_id=${encodeURIComponent(tenantId)}`,{headers:token===""?{}:{authorization:`Bearer ${token}`},cache:"no-store"});if(!response.ok)throw await responseError(response);return await response.json() as {retention:{days:number;updated_at:string};holds:Array<Record<string,unknown>>};}

export async function revealCredential(
  resourceType: "subscriber" | "nas",
  resourceId: string,
  tenantId: string,
  token: string,
): Promise<string> {
  const response = await fetch(
    `/api/v1/credentials/${resourceType}/${encodeURIComponent(resourceId)}?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return ((await response.json()) as { credential: string }).credential;
}

export async function loadRadiusLogs(
  tenantId: string,
  token: string,
  search = "",
): Promise<{ source: string; entries: string[] }> {
  const query = new URLSearchParams({ tenant_id: tenantId, lines: "250" });
  if (search.trim()) query.set("search", search.trim());
  const response = await fetch(`/api/v1/diagnostics/radius-logs?${query}`, {
    headers: token === "" ? {} : { authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as { source: string; entries: string[] };
}

export async function loadOperators(
  tenantId: string,
  token: string,
): Promise<Record<string, unknown>[]> {
  const response = await fetch(
    `/api/v1/operators?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as Record<string, unknown>[];
}
export async function loadOperatorRoles(
  tenantId: string,
  token: string,
): Promise<Record<string, unknown>[]> {
  const response = await fetch(
    `/api/v1/operator-roles?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as Record<string, unknown>[];
}
export async function loadOperatorPermissions(
  tenantId: string,
  token: string,
): Promise<Record<string, unknown>[]> {
  const response = await fetch(
    `/api/v1/operator-permissions?tenant_id=${encodeURIComponent(tenantId)}`,
    {
      headers: token === "" ? {} : { authorization: `Bearer ${token}` },
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as Record<string, unknown>[];
}
export interface ReportDefinition {
  id: string;
  version: number;
  name: string;
  classification: string;
  fields: string[];
  maximumRows: number;
  maximumPeriodDays: number;
}
export async function loadReports(
  tenantId: string,
  token: string,
): Promise<ReportDefinition[]> {
  return get<ReportDefinition[]>("/api/v1/reports", tenantId, token);
}
export async function runReport(
  tenantId: string,
  token: string,
  csrf: string,
  definitionId: string,
  options: { siteId?: string; from: string; to: string; limit: number },
) {
  return createResource<{
    runId: string;
    definition: ReportDefinition;
    rowCount: number;
    checksum: string;
    rows: Record<string, unknown>[];
  }>(
    `/api/v1/reports/${encodeURIComponent(definitionId)}/runs`,
    tenantId,
    token,
    {
      ...(options.siteId ? { site_id: options.siteId } : {}),
      from: options.from,
      to: options.to,
      limit: options.limit,
    },
    csrf,
  );
}
export async function exportReport(
  tenantId: string,
  token: string,
  csrf: string,
  definitionId: string,
  options: {
    siteId?: string;
    from: string;
    to: string;
    format: "csv" | "json" | "xlsx" | "pdf" | "html";
  },
) {
  const response = await fetch(
    `/api/v1/reports/${encodeURIComponent(definitionId)}/exports`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
        "x-csrf-token": csrf,
      },
      body: JSON.stringify({
        tenant_id: tenantId,
        correlation_id: newCorrelationId(),
        ...(options.siteId ? { site_id: options.siteId } : {}),
        from: options.from,
        to: options.to,
        limit: 5000,
        format: options.format,
      }),
      cache: "no-store",
    },
  );
  if (!response.ok) throw await responseError(response);
  return {
    blob: await response.blob(),
    filename: (
      response.headers
        .get("content-disposition")
        ?.match(/filename=([^;]+)/)?.[1] ?? `report.${options.format}`
    ).replaceAll('"', ""),
  };
}
export async function loadSupportCases(
  tenantId: string,
  token: string,
  search = "",
) {
  const query = new URLSearchParams({
    tenant_id: tenantId,
    limit: "100",
    offset: "0",
  });
  if (search.trim()) query.set("search", search.trim());
  const response = await fetch(`/api/v1/support/cases?${query}`, {
    headers: { authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as {
    data: Record<string, unknown>[];
    total: number;
  };
}
export async function loadGuestEnrollments(tenantId: string, token: string) {
  const response = await fetch(
    `/api/v1/guest/enrollments?tenant_id=${encodeURIComponent(tenantId)}&limit=100&offset=0`,
    { headers: { authorization: `Bearer ${token}` }, cache: "no-store" },
  );
  if (!response.ok) throw await responseError(response);
  return (await response.json()) as {
    data: Record<string, unknown>[];
    total: number;
  };
}
export async function loadGuestPolicies(tenantId: string, token: string) {
  return get<Record<string, unknown>[]>(
    "/api/v1/guest/policies",
    tenantId,
    token,
  );
}

export async function loadWorkspace(
  tenantId: string,
  token: string,
): Promise<WorkspaceData> {
  const [
    dashboard,
    subscribers,
    packages,
    billing,
    payments,
    expenses,
    vouchers,
    nas,
    sessions,
    sites,
    edges,
    edgeConflicts,
  ] = await Promise.all([
    get<DashboardData>("/api/v1/dashboard", tenantId, token),
    get<Record<string, unknown>[]>(
      "/api/v1/subscribers?limit=25",
      tenantId,
      token,
    ),
    get<Record<string, unknown>[]>(
      "/api/v1/packages?limit=200",
      tenantId,
      token,
    ),
    get<Record<string, unknown>[]>("/api/v1/invoices", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/payments", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/expenses", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/voucher-batches", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/nas", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/aaa-sessions", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/sites?limit=200", tenantId, token),
    get<Record<string, unknown>[]>("/api/v1/edge", tenantId, token).catch(
      () => [],
    ),
    get<Record<string, unknown>[]>(
      "/api/v1/edge/reconciliation",
      tenantId,
      token,
    ).catch(() => []),
  ]);
  return {
    dashboard,
    subscribers,
    packages,
    billing,
    payments,
    expenses,
    vouchers,
    nas,
    sessions,
    sites,
    edges,
    edgeConflicts,
  };
}

export async function createResource<T = Record<string, unknown>>(
  path: string,
  tenantId: string,
  token: string,
  body: Record<string, unknown>,
  csrf = "",
  method: "POST" | "PUT" = "POST",
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      "content-type": "application/json",
      ...(token === "" ? {} : { authorization: `Bearer ${token}` }),
      ...(csrf === "" ? {} : { "x-csrf-token": csrf }),
    },
    body: JSON.stringify({
      ...body,
      tenant_id: tenantId,
      correlation_id: newCorrelationId(),
    }),
    cache: "no-store",
  });
  if (!response.ok) {
    throw await responseError(response);
  }
  return (await response.json()) as T;
}
